import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import useAxiosInstance from "../../../../Utilities/Hooks/useAxiosInstance";
import { ORDERS_API } from "../../../../Utilities/APIs/APIs";

function OrderInvoicePackage({ targetID }) {
  const axiosInstance = useAxiosInstance();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    async function fetchOrder() {
      const { data } = await axiosInstance.get(
        ORDERS_API + "getSingleOrder/" + targetID
      );

      setOrder(data[0]);
    }

    fetchOrder();
  }, [axiosInstance, targetID]);

  /*
  =========================================
  EXCHANGE
  =========================================
  */

  const hasExchange =
    Number(order?.previousOrderTotal || 0) > 0;

  const previousOrderTotal =
    Number(order?.previousOrderTotal || 0);

  const updatedOrderTotal =
    Number(order?.orderTotal || 0);

  const exchangeDifference =
    updatedOrderTotal -
    previousOrderTotal;

  const isRefund =
    exchangeDifference < 0;

  const exchangeAmount =
    Math.abs(exchangeDifference);

  const invoiceRef = useRef();

  const generatePDF = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: `Invoice_${order?.invoiceNo}`,
  });

  const USD_RATE = 125;
  const USD_MULTIPLIER = 3;

  const isInternational =
    order?.customerCountry &&
    order?.customerCountry !== "Bangladesh" &&
    order?.customerCountry !== "BD";

  const currencyLabel = isInternational ? "USD" : "৳";

  const convertProductToUSD = (amount = 0) =>
    (Number(amount || 0) * USD_MULTIPLIER) / USD_RATE;

  const money = (amount = 0) => {
    const num = Number(amount || 0);

    if (!Number.isFinite(num)) {
      return isInternational ? "USD 0.00" : "৳ 0";
    }

    if (isInternational) {
      return `USD ${convertProductToUSD(num).toFixed(0)}`;
    }

    return `৳ ${Math.round(num)}`;
  };

  const totalMoney = () => {
    if (isInternational && order?.displayTotalUSD) {
      return `USD ${Number(order.displayTotalUSD).toFixed(0)}`;
    }

    return money(order?.orderTotal);
  };

  return (
    <div>
      <button
        onClick={generatePDF}
        style={{
          padding: "12px 25px",
          backgroundColor: "#007BFF",
          color: "#FFF",
          border: "none",
          borderRadius: "5px",
          fontFamily: "Roboto, Arial, sans-serif",
          fontSize: "16px",
          cursor: "pointer",
        }}
      >
        Print Invoice
      </button>

      <div
        ref={invoiceRef}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#FFF",
          fontFamily: "Roboto, Arial, sans-serif",
          color: "#000",
          position: "relative",
        }}
      >
        {/* PRINT CSS */}

        <style>
          {`
            @page {
              size: 4in 6in;
              margin: 0;
            }

            body {
              margin: 0;
              padding: 0;
              font-family: Roboto, Arial, sans-serif;
            }

            @media print {
              .watermark {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-size: 70px;
                font-weight: bold;
                color: rgba(200, 0, 0, 0.15);
                z-index: 0;
                pointer-events: none;
                text-transform: uppercase;
              }
            }
          `}
        </style>

        {/* WATERMARK */}

        {(order?.paymentMethod ===
          "bkash" ||
          order?.paymentMethod ===
            "SSLCommerz") && (
          <div
            className="watermark"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform:
                "translate(-50%, -50%)",
              fontSize: "70px",
              fontWeight: "bold",
              color:
                "rgba(200, 0, 0, 0.15)",
              zIndex: 0,
              pointerEvents: "none",
              textTransform: "uppercase",
            }}
          >
            PAID
          </div>
        )}

        {/* HEADER */}

        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
            zIndex: 1,
          }}
        >
          <h1
            style={{
              margin: "0",
              fontSize: "22px",
              fontWeight: "bold",
            }}
          >
            Invoice
          </h1>
        </div>

        {/* ORDER INFO */}

        <div
          style={{
            marginBottom: "30px",
            zIndex: 1,
          }}
        >
          <p
            style={{
              margin: "12px 0",
              fontSize: "18px",
            }}
          >
            <b>Order Number:</b>{" "}
            {order?.invoiceNo}
          </p>

          <p
            style={{
              margin: "12px 0",
              fontSize: "18px",
            }}
          >
            <b>Order Date:</b>{" "}
            {new Date(
              order?.createdAt
            ).toLocaleDateString()}
          </p>
        </div>

        {/* CUSTOMER */}

        <div
          style={{
            marginBottom: "35px",
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              marginBottom: "10px",
              color: "grey",
            }}
          >
            Customer Information
          </h2>

          <p
            style={{
              margin: "10px 0",
              fontSize: "18px",
            }}
          >
            <b>Name:</b>{" "}
            {order?.shippingName}
          </p>

          <p
            style={{
              margin: "10px 0",
              fontSize: "18px",
            }}
          >
            <b>Phone:</b>{" "}
            {order?.shippingPhone}
          </p>

          <p
            style={{
              margin: "10px 0",
              fontSize: "18px",
            }}
          >
            <b>Address:</b>{" "}
            {order?.shippingAddress},{" "}
            {order?.customerArea},{" "}
            {order?.customerDistrict}
            {order?.postalCode ? ` ${order?.postalCode}` : ""}, {" "}
            {order?.customerCountry || "Bangladesh"}
          </p>
        </div>

        {/* ORDER SUMMARY */}

        <div
          style={{
            marginBottom: "35px",
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              marginBottom: "10px",
              color: "grey",
            }}
          >
            Order Summary
          </h2>

          {/* NORMAL ORDER */}

          {!hasExchange ? (
            <p
              style={{
                margin: "10px 0",
                fontSize: "18px",
              }}
            >
              <b>Total Amount:</b> {" "}
              {totalMoney()}
            </p>
          ) : (
            <>
              <p
                style={{
                  margin: "10px 0",
                  fontSize: "18px",
                }}
              >
                <b>
                  Updated Order Total:
                </b>{" "}
                ৳{" "}
                {Math.round(
                  updatedOrderTotal
                )}
              </p>

              <p
                style={{
                  margin: "10px 0",
                  fontSize: "18px",
                }}
              >
                <b>
                  Customer Paid Before:
                </b>{" "}
                ৳{" "}
                {Math.round(
                  previousOrderTotal
                )}
              </p>

              <p
                style={{
                  margin: "10px 0",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {isRefund
                  ? "Customer Refund:"
                  : "Customer Will Pay:"}{" "}
                ৳{" "}
                {Math.round(
                  exchangeAmount
                )}
              </p>
            </>
          )}

          <p
            style={{
              margin: "10px 0",
              fontSize: "18px",
            }}
          >
            <b>Delivery Method:</b>{" "}
            <span
              style={{
                color:
                  order?.shippingMethod ===
                  "express"
                    ? "red"
                    : "black",
              }}
            >
              {order?.shippingMethod}
            </span>
          </p>

          <p
            style={{
              margin: "10px 0",
              fontSize: "18px",
            }}
          >
            <b>Payment Method:</b>{" "}
            <span
              style={{
                color:
                  order?.paymentMethod ===
                    "bkash" ||
                  order?.paymentMethod ===
                    "SSLCommerz"
                    ? "red"
                    : "black",
              }}
            >
              {order?.paymentMethod ===
              "cash"
                ? "Cash on Delivery"
                : order?.paymentMethod ===
                  "bkash"
                ? "Paid via Bkash"
                : order?.paymentMethod ===
                  "SSLCommerz"
                ? "Paid via SSLCommerz"
                : order?.paymentMethod}
            </span>
          </p>

          <p
            style={{
              margin: "10px 0",
              fontSize: "18px",
            }}
          >
            <b>
              Additional Instructions:
            </b>{" "}
            {
              order?.additionalInstructions
            }
          </p>
        </div>

        {/* FOOTER */}

        <div
          style={{
            textAlign: "center",
            marginTop: "40px",
            fontSize: "16px",
          }}
        >
          <p
            style={{
              margin: "0",
              fontSize: "16px",
              fontWeight: "bold",
              color: "#f26822",
              textAlign: "center",
            }}
          >
            {order?.gifted === "true"
              ? "Gift"
              : ""}
          </p>

          <p>
            Thank you for your order!
          </p>
        </div>
      </div>
    </div>
  );
}

export default OrderInvoicePackage;